import { EntityKey } from '../ap-document/ap-document';
import { EntityKeyByReference } from '../bank-account/bank-account';
import { BankTransaction } from '../bank-transaction/bank-transaction';
import { S3Object } from '../ap-payment/ap-payment';

/**
 * Where a transfer stands (#5088). Mirrors `Sierra.Finance.Models.Enums.InternalTransferStatus` —
 * declaration order is the wire format, so these numbers must not be reordered.
 *
 * Stored on the API rather than derived from the legs: CANCELLED has no legs to read, and RETURNED is
 * leg-for-leg identical to RECEIVED except for which account the incoming leg lands in.
 */
export enum InternalTransferStatus {
  DRAFT = 0,
  IN_TRANSIT = 1,
  RECEIVED = 2,
  RETURNED = 3,
  CANCELLED = 4,
}

/** GlobalEntityDefinition.CashRequest (51) — the entity code for the linked request. */
export const CASH_REQUEST_ENTITY_CODE = 51;

export interface InternalTransfer {
  id?: string;
  number?: string | null;
  status: number;
  financialCompanyReference: EntityKeyByReference;

  /** Source and destination BankAccount codes. Frozen once the transfer is sent. */
  fromAccountCode?: string | null;
  toAccountCode?: string | null;

  /**
   * Gross amount to move, before any charge — read-only. The API reads it off the outgoing leg rather than
   * storing it: that movement is what takes the money out of the source bank, so its amount *is* the
   * transfer amount. To change it, edit the outgoing leg.
   */
  readonly amount: number;

  /** The currency the money LEAVES in — the source account's, and what cash in transit holds until the
   *  transfer clears. */
  currency: number;

  /** The currency the money LANDS in when the bank converts on the way; null when it does not, which is
   *  the ordinary transfer. */
  destinationCurrency?: number | null;

  /**
   * What actually arrived, in {@link destinationCurrency} — read off the incoming leg, never stored.
   * Zero until the receipt is confirmed, because the figure follows from the rate the bank quoted and a
   * draft receipt has no rate yet. Equal to {@link amount} when nothing converted.
   */
  readonly amountArrived: number;

  /** The rate the bank gave, in units of the non-USD side per 1 USD — the number on the wire
   *  confirmation. Set only on a converted transfer, once its receipt is confirmed. */
  exchangeRateToUsd?: number | null;

  transferDate: string;

  /** The OUTCOME movement out of the source account. Null until sent. */
  outgoingKey?: EntityKey | null;
  /** The INCOME movement into the destination account — or, on a return, back into the source. */
  incomingKey?: EntityKey | null;

  /** Both hydrated on every read, batched on lists — the figures live on them, not on the transfer. */
  outgoingLeg?: BankTransaction | null;
  incomingLeg?: BankTransaction | null;

  /** The Approved Cash Request this transfer answers, if any. Most transfers have none. */
  cashRequest?: EntityKey | null;

  /** Required on a returned transfer, absent otherwise. */
  returnReason?: string | null;

  exchangeRate?: number | null;
  notes?: string | null;
  attachments?: S3Object[];

  createdDate?: string;
  createdBy?: string;
  modifiedDate?: string;
  modifiedBy?: string;
}

/** Create request. Company and currency are derived API-side, so neither is sent. */
export interface InternalTransferInsert {
  fromAccountCode: string;
  toAccountCode: string;
  transferDate: string;
  cashRequest?: EntityKey | null;
  notes?: string | null;
}

/**
 * The accounts are absent on purpose: they are chosen in step 1 and locked from then on, and the way out of
 * a wrong pair is to cancel and start over. Sending them would also race the async account resolve.
 */
export interface InternalTransferUpdate {
  transferDate: string;
  cashRequest?: EntityKey | null;
  notes?: string | null;
}

/**
 * A return needs a reason and nothing else. What came back, what the banks took and when it landed are on
 * the incoming leg, saved through the ordinary bank transaction endpoint like any other movement.
 */
export interface InternalTransferReturnCommand {
  reason: string;
}

/**
 * Confirming what landed. Empty on an ordinary transfer — everything a receive needs is already on the
 * incoming leg — and carrying one figure when the bank converted.
 */
export interface InternalTransferReceiveCommand {
  /**
   * The rate the bank gave, in units of the non-USD currency per 1 USD ("24.65 Lempiras to the dollar").
   * Required when the transfer converts, ignored when it does not.
   */
  exchangeRateToUsd?: number | null;
}

export interface InternalTransferSearchCommand {
  number?: string | null;
  bankAccountCode?: string | null;
  cashRequestNumber?: string | null;
  notes?: string | null;
  financialCompanyIds?: string[] | null;
  statuses?: number[] | null;
  from?: string | null;
  to?: string | null;
}
